import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  collection, query, where, getDocs
} from 'firebase/firestore';
import { db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { canCreateStudent, getPlanLimits } from '../utils/planLimits';

interface Student {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  studentId: string;
  className?: string;
  mustChangePassword?: boolean;
  passwordSetByTeacher?: boolean;
  passwordSetAt?: any;
  lastActivity?: any;
  createdAt: any;
}

interface ClassTab {
  name: string;
  count: number;
}

// Initialize Firebase Functions
const functions = getFunctions();

export default function StudentManagerClean() {
  const { user, userDoc } = useAuth();
  const navigate = useNavigate();

  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedClass, setSelectedClass] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [credentialsModal, setCredentialsModal] = useState<{
    open: boolean;
    studentName: string;
    username: string;
    email: string;
    tempPassword: string;
  } | null>(null);

  const [newStudent, setNewStudent] = useState({
    firstName: '',
    lastName: '',
    email: '',
    studentId: '',
    className: ''
  });

  const studentsPerPage = 25;

  // Load students
  useEffect(() => {
    if (!user?.uid) return;
    loadStudents();
  }, [user?.uid]);

  async function loadStudents() {
    try {
      setLoading(true);
      const q = query(
        collection(db, "users"),
        where("teacherId", "==", user?.uid),
        where("role", "==", "student")
      );
      const snap = await getDocs(q);
      const list: Student[] = snap.docs.map(d => ({
        id: d.id,
        ...d.data()
      } as Student));
      setStudents(list);
    } catch (err) {
      console.error(err);
      setMessage('Failed to load students');
    } finally {
      setLoading(false);
    }
  }

  // Calculate class tabs
  const classTabs: ClassTab[] = useMemo(() => {
    const classMap = new Map<string, number>();
    students.forEach(s => {
      const className = s.className || 'Unassigned';
      classMap.set(className, (classMap.get(className) || 0) + 1);
    });

    const tabs = [{ name: 'All', count: students.length }];
    classMap.forEach((count, name) => {
      tabs.push({ name, count });
    });
    return tabs;
  }, [students]);

  // Filter and search students
  const filteredStudents = useMemo(() => {
    let filtered = students;

    // Filter by class
    if (selectedClass !== 'All') {
      filtered = filtered.filter(s => (s.className || 'Unassigned') === selectedClass);
    }

    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(s =>
        s.firstName.toLowerCase().includes(q) ||
        s.lastName.toLowerCase().includes(q) ||
        s.email.toLowerCase().includes(q) ||
        s.studentId?.toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [students, selectedClass, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);
  const paginatedStudents = useMemo(() => {
    const start = (currentPage - 1) * studentsPerPage;
    return filteredStudents.slice(start, start + studentsPerPage);
  }, [filteredStudents, currentPage]);

  // Get student status
  const getStudentStatus = (student: Student) => {
    if (!student.lastActivity) {
      return { label: 'Never Logged In', color: 'text-gray-400', icon: '⚪' };
    }

    const lastActivityDate = student.lastActivity?.toDate ? student.lastActivity.toDate() : new Date(student.lastActivity);
    const daysSinceActivity = Math.floor((Date.now() - lastActivityDate.getTime()) / (1000 * 60 * 60 * 24));

    if (daysSinceActivity <= 7) {
      return { label: 'Active', color: 'text-green-600', icon: '🟢' };
    } else {
      return { label: 'Inactive', color: 'text-yellow-600', icon: '🟡' };
    }
  };

  // Create student using secure Cloud Function
  async function handleCreateStudent(e: React.FormEvent) {
    e.preventDefault();

    if (!newStudent.firstName || !newStudent.lastName) {
      setMessage('Please fill all required fields');
      return;
    }

    try {
      setCreating(true);
      setMessage('');

      // Call the secure Cloud Function
      const createStudent = httpsCallable(functions, 'createStudentAccount');
      const result: any = await createStudent({
        firstName: newStudent.firstName,
        lastName: newStudent.lastName,
        studentId: newStudent.studentId || undefined,
        className: newStudent.className || undefined,
        personalEmail: newStudent.email || undefined,
      });

      if (result.data.success) {
        // Show credentials modal immediately
        setCredentialsModal({
          open: true,
          studentName: result.data.displayName,
          username: result.data.studentId,
          email: result.data.email,
          tempPassword: result.data.tempPassword
        });

        setMessage(`✓ Student created: ${result.data.displayName}`);
        setNewStudent({
          firstName: '',
          lastName: '',
          email: '',
          studentId: '',
          className: ''
        });
        setShowForm(false);
        await loadStudents();
      }
    } catch (err: any) {
      console.error('Error creating student:', err);

      // Handle Firebase Function errors
      const errorMessage = err.message || 'Failed to create student account';
      setMessage(`Error: ${errorMessage}`);
    } finally {
      setCreating(false);
    }
  }

  // Delete student
  async function handleDeleteStudent(studentUid: string, studentEmail: string) {
    if (!confirm(`Delete student ${studentEmail}?`)) return;

    try {
      setMessage('Deleting...');

      // ✅ Call secure Cloud Function (deletes Auth + Firestore + updates counter atomically)
      const deleteStudent = httpsCallable(functions, 'deleteStudentAccountSecure');
      await deleteStudent({ studentUid });

      setMessage('✓ Student deleted');
      await loadStudents();
    } catch (err: any) {
      console.error('Error deleting student:', err);
      const errorMessage = err.message || 'Failed to delete student';
      setMessage(`Error: ${errorMessage}`);
    }
  }

  // Print login sheets
  const handlePrintLoginSheets = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const studentsToPrint = selectedClass === 'All' ? students : filteredStudents;

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Student Login Sheets - ${selectedClass}</title>
        <style>
          body { font-family: Arial, sans-serif; }
          h1 { text-align: center; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
          th { background-color: #f2f2f2; font-weight: bold; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .header { margin-bottom: 20px; }
          @media print {
            button { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Student Login Credentials - ${selectedClass}</h1>
          <p><strong>Class:</strong> ${selectedClass}</p>
          <p><strong>Total Students:</strong> ${studentsToPrint.length}</p>
          <p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
        </div>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Student ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Password</th>
              <th>Class</th>
            </tr>
          </thead>
          <tbody>
            ${studentsToPrint.map((s, idx) => `
              <tr>
                <td>${idx + 1}</td>
                <td>${s.studentId || '-'}</td>
                <td>${s.firstName} ${s.lastName}</td>
                <td>${s.email}</td>
                <td>${s.password || '[Set by teacher]'}</td>
                <td>${s.className || 'Unassigned'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <button onclick="window.print()" style="margin: 20px; padding: 10px 20px; font-size: 16px;">Print</button>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedStudents.length === 0) return;
    if (!confirm(`Delete ${selectedStudents.length} selected students?`)) return;

    try {
      setMessage(`Deleting ${selectedStudents.length} students...`);

      // ✅ Call Cloud Function for each student (atomic per-student)
      const deleteStudent = httpsCallable(functions, 'deleteStudentAccountSecure');

      let successCount = 0;
      let failCount = 0;

      for (const studentUid of selectedStudents) {
        try {
          await deleteStudent({ studentUid });
          successCount++;
        } catch (err) {
          console.error(`Failed to delete student ${studentUid}:`, err);
          failCount++;
        }
      }

      if (failCount === 0) {
        setMessage(`✓ Deleted ${successCount} students`);
      } else {
        setMessage(`Deleted ${successCount} students, ${failCount} failed`);
      }

      setSelectedStudents([]);
      await loadStudents();
    } catch (err: any) {
      setMessage(`Error: ${err.message}`);
    }
  };

  const planLimits = getPlanLimits(userDoc);

  if (loading) {
    return <div className="p-8">Loading students...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to Dashboard"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Student Manager</h1>
          </div>

          <div className="flex items-center gap-3">
            <div className="text-sm text-gray-600">
              <span className="font-semibold">{students.length}</span> / {planLimits.studentsLimit} students
            </div>
            <button
              onClick={() => setShowForm(!showForm)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!canCreateStudent(userDoc, students.length).ok}
            >
              + Add Student
            </button>
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`max-w-7xl mx-auto mb-4 p-4 rounded-lg ${
          message.startsWith('✓') ? 'bg-green-50 text-green-800' :
          message.startsWith('Error') ? 'bg-red-50 text-red-800' :
          'bg-blue-50 text-blue-800'
        }`}>
          {message}
        </div>
      )}

      {/* Create Student Form */}
      {showForm && (
        <div className="max-w-7xl mx-auto mb-6 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">Add New Student</h2>
          <form onSubmit={handleCreateStudent} className="grid grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="First Name *"
              value={newStudent.firstName}
              onChange={e => setNewStudent({...newStudent, firstName: e.target.value})}
              className="px-4 py-2 border rounded-lg"
              required
            />
            <input
              type="text"
              placeholder="Last Name *"
              value={newStudent.lastName}
              onChange={e => setNewStudent({...newStudent, lastName: e.target.value})}
              className="px-4 py-2 border rounded-lg"
              required
            />
            <input
              type="email"
              placeholder="Personal Email (optional, for teacher records)"
              value={newStudent.email}
              onChange={e => setNewStudent({...newStudent, email: e.target.value})}
              className="px-4 py-2 border rounded-lg"
            />
            <input
              type="text"
              placeholder="Student ID (optional)"
              value={newStudent.studentId}
              onChange={e => setNewStudent({...newStudent, studentId: e.target.value})}
              className="px-4 py-2 border rounded-lg"
            />
            <input
              type="text"
              placeholder="Class Name (optional)"
              value={newStudent.className}
              onChange={e => setNewStudent({...newStudent, className: e.target.value})}
              className="px-4 py-2 border rounded-lg"
            />
            <div className="col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
              <p className="text-sm text-blue-800">
                <strong>🔒 Secure Password:</strong> A strong temporary password will be auto-generated.
                The student will be required to change it on first login.
              </p>
              <p className="text-sm text-blue-800">
                <strong>👤 Student Login:</strong> Students will login using their auto-generated username (e.g., jsmith_1234) and the temporary password. No email needed!
              </p>
            </div>
            <div className="col-span-2 flex gap-3">
              <button
                type="submit"
                disabled={creating}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:bg-gray-400"
              >
                {creating ? 'Creating...' : 'Create Student'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-semibold"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Class Tabs */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex flex-wrap gap-2">
          {classTabs.map(tab => (
            <button
              key={tab.name}
              onClick={() => {
                setSelectedClass(tab.name);
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                selectedClass === tab.name
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-blue-500'
              }`}
            >
              {tab.name} ({tab.count})
            </button>
          ))}
        </div>
      </div>

      {/* Search and Actions */}
      <div className="max-w-7xl mx-auto mb-6 bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center justify-between gap-4">
          <input
            type="text"
            placeholder="Search students..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="flex-1 px-4 py-2 border rounded-lg"
          />
          <button
            onClick={handlePrintLoginSheets}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print Login Sheets
          </button>
          {selectedStudents.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
            >
              Delete {selectedStudents.length} Selected
            </button>
          )}
        </div>
      </div>

      {/* Students Table */}
      <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-100 border-b-2 border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedStudents.length === paginatedStudents.length && paginatedStudents.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedStudents(paginatedStudents.map(s => s.id));
                    } else {
                      setSelectedStudents([]);
                    }
                  }}
                  className="w-4 h-4"
                />
              </th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Student ID</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Name</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Email</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Class</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Password Status</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Login Status</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedStudents.map((student) => {
              const status = getStudentStatus(student);
              return (
                <tr key={student.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(student.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedStudents([...selectedStudents, student.id]);
                        } else {
                          setSelectedStudents(selectedStudents.filter(id => id !== student.id));
                        }
                      }}
                      className="w-4 h-4"
                    />
                  </td>
                  <td className="px-4 py-3 text-gray-700">{student.studentId || '-'}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{student.firstName} {student.lastName}</td>
                  <td className="px-4 py-3 text-gray-700">{student.email}</td>
                  <td className="px-4 py-3 text-gray-700">{student.className || 'Unassigned'}</td>
                  <td className="px-4 py-3">
                    {student.mustChangePassword ? (
                      <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full font-medium">
                        🔒 Temp Password
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full font-medium">
                        ✓ Changed
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`flex items-center gap-1 ${status.color} font-semibold text-sm`}>
                      <span>{status.icon}</span>
                      {status.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleDeleteStudent(student.id, student.email)}
                      className="text-red-600 hover:text-red-800 font-semibold text-sm"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t">
            <div className="text-sm text-gray-600">
              Showing {((currentPage - 1) * studentsPerPage) + 1} - {Math.min(currentPage * studentsPerPage, filteredStudents.length)} of {filteredStudents.length}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 border rounded-lg ${
                    currentPage === page ? 'bg-blue-600 text-white' : 'hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {filteredStudents.length === 0 && (
        <div className="max-w-7xl mx-auto mt-8 text-center text-gray-500">
          No students found. {searchQuery && 'Try a different search.'}
        </div>
      )}

      {/* Credentials Modal */}
      {credentialsModal?.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-8">
            <div className="text-center mb-6">
              <div className="text-5xl mb-4">🎉</div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Student Account Created!</h2>
              <p className="text-gray-600">Save these credentials now - they won't be shown again</p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 mb-6 border-2 border-blue-200">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-600 block mb-1">Student Name</label>
                  <div className="text-lg font-bold text-gray-900">{credentialsModal.studentName}</div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-600 block mb-1">Username / Student ID</label>
                  <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-300">
                    <code className="text-lg font-mono font-bold text-blue-600">{credentialsModal.username}</code>
                    <button
                      onClick={() => navigator.clipboard.writeText(credentialsModal.username)}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-semibold"
                    >
                      Copy
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-600 block mb-1">Email</label>
                  <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-300">
                    <code className="text-lg font-mono text-gray-700">{credentialsModal.email}</code>
                    <button
                      onClick={() => navigator.clipboard.writeText(credentialsModal.email)}
                      className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm font-semibold"
                    >
                      Copy
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-600 block mb-1">Temporary Password</label>
                  <div className="flex items-center justify-between bg-yellow-50 rounded-lg p-3 border-2 border-yellow-400">
                    <code className="text-lg font-mono font-bold text-yellow-900">{credentialsModal.tempPassword}</code>
                    <button
                      onClick={() => navigator.clipboard.writeText(credentialsModal.tempPassword)}
                      className="px-3 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-sm font-semibold"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <span className="text-2xl">⚠️</span>
                <div className="flex-1">
                  <h3 className="font-bold text-yellow-900 mb-1">Important Security Notice</h3>
                  <ul className="text-sm text-yellow-800 space-y-1">
                    <li>• This temporary password will <strong>NOT be shown again</strong></li>
                    <li>• Student must change password on first login</li>
                    <li>• Print or save these credentials immediately</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  const printContent = `
                    <html>
                    <head>
                      <title>Student Login Credentials</title>
                      <style>
                        body { font-family: Arial, sans-serif; padding: 40px; }
                        h1 { color: #1e40af; }
                        .cred-box { background: #f0f9ff; padding: 20px; border-radius: 8px; border: 2px solid #3b82f6; margin: 20px 0; }
                        .label { font-weight: bold; color: #64748b; font-size: 14px; }
                        .value { font-size: 18px; font-weight: bold; margin: 5px 0 15px 0; font-family: monospace; }
                        .warning { background: #fef3c7; padding: 15px; border-radius: 8px; border-left: 4px solid #f59e0b; margin-top: 20px; }
                      </style>
                    </head>
                    <body>
                      <h1>🔐 ExamLock Student Login Credentials</h1>
                      <div class="cred-box">
                        <div class="label">Student Name:</div>
                        <div class="value">${credentialsModal.studentName}</div>

                        <div class="label">Username / Student ID:</div>
                        <div class="value">${credentialsModal.username}</div>

                        <div class="label">Email:</div>
                        <div class="value">${credentialsModal.email}</div>

                        <div class="label">Temporary Password:</div>
                        <div class="value">${credentialsModal.tempPassword}</div>
                      </div>
                      <div class="warning">
                        <strong>⚠️ First Login Instructions:</strong><br>
                        1. Go to the ExamLock student login page<br>
                        2. Login with the credentials above<br>
                        3. You will be required to create a new password<br>
                        4. Choose a strong, memorable password
                      </div>
                    </body>
                    </html>
                  `;
                  const printWindow = window.open('', '_blank');
                  if (printWindow) {
                    printWindow.document.write(printContent);
                    printWindow.document.close();
                    printWindow.print();
                  }
                }}
                className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print Credentials
              </button>
              <button
                onClick={() => setCredentialsModal(null)}
                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
              >
                Done - I've Saved It
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
